/**
 * Dealer Smart Return Offers Tab
 * Configures Smart Return Offers settings for a specific dealer
 * Part of Dealer Management in Master Admin UI
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Bell, Clock, Target, MessageSquare, Calendar, CreditCard, Car, Phone, PhoneCall, Link as LinkIcon, CheckCircle, AlertCircle, Settings, Palette } from "lucide-react";

// CTA Module definitions with icons
const CTA_MODULES = [
  { id: "vip_appointment", label: "VIP Appointment", icon: Calendar, description: "Schedule a priority appointment" },
  { id: "preapproval", label: "Quick Pre-Approval", icon: CreditCard, description: "Get financing pre-approval" },
  { id: "trade_value", label: "Trade-In Value", icon: Car, description: "Get trade-in estimate" },
  { id: "sales_contact", label: "Talk to Sales", icon: Phone, description: "Connect with sales team" },
  { id: "credit_check", label: "Credit Check", icon: CreditCard, description: "Check credit options" },
  { id: "request_callback", label: "Request Callback", icon: PhoneCall, description: "Request a callback" },
];

const SIZE_OPTIONS = ["small", "medium", "large"];

const DealerSmartOffersTab = ({ dealer, onRefresh, disabled = false }) => {
  const { apiFetch } = useAuth();
  const { success: showToast, error: showError } = useToast();
  
  const [settings, setSettings] = useState({
    enabled: false,
    trigger_mode: "returning_only",  // iter121 — 'returning_only' | 'every_visitor'
    gap_hours: 24,
    cooldown_hours: 72,
    intent_threshold_enabled: true,
    target_inventory: true,
    target_vdp: true,
    target_homepage: false,
    enabled_modules: ["vip_appointment", "sales_contact"],
    headline: "Welcome Back!",
    subtext: "We noticed you're interested. How can we help today?",
    disclaimer_text: "",
    button_labels: {},
    button_urls: {},  // NEW: URLs for each CTA button
    sales_phone: "",
    sales_sms: "",
    button_size: "medium",
    headline_size: "medium",
    primary_color: "#2563eb",
    secondary_color: "#10b981",
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load settings for this dealer
  const loadSettings = useCallback(async () => {
    if (!dealer?.id) return;
    
    setLoading(true);
    setMessage(null);
    
    try {
      const res = await apiFetch("/smart-offers/settings", {
        method: "GET",
        headers: { "X-Viewing-Dealer": dealer.id }
      });
      
      if (res.data) {
        setSettings(prev => ({
          ...prev,
          ...res.data,
          button_urls: res.data.button_urls || {},
        }));
      }
    } catch (err) {
      console.error("[SmartOffersTab] Load error:", err);
      // 404 is OK - no settings yet
      if (err?.response?.status !== 404) {
        setMessage({ type: "error", text: "Failed to load settings" });
      }
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dealer?.id]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save settings
  const saveSettings = async () => {
    if (disabled) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await apiFetch("/smart-offers/settings", {
        method: "PUT",
        headers: { "X-Viewing-Dealer": dealer.id },
        data: settings,
      });
      
      if (response.data?.success || response.status === 200) {
        setMessage({ type: "success", text: "Settings saved successfully!" });
        showToast("Popup Offers settings saved!");
        if (onRefresh) onRefresh();
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      console.error("[SmartOffersTab] Save error:", err);
      const errorMsg = err?.response?.data?.detail || "Failed to save settings";
      setMessage({ type: "error", text: errorMsg });
      showError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    if (disabled) return;
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleModuleToggle = (moduleId) => {
    if (disabled) return;
    setSettings(prev => {
      const current = prev.enabled_modules || [];
      if (current.includes(moduleId)) {
        if (current.length <= 1) return prev; // Keep at least one
        return { ...prev, enabled_modules: current.filter(m => m !== moduleId) };
      } else {
        if (current.length >= 4) return prev; // Max 4
        return { ...prev, enabled_modules: [...current, moduleId] };
      }
    });
  };

  const handleButtonLabelChange = (moduleId, label) => {
    if (disabled) return;
    setSettings(prev => ({
      ...prev,
      button_labels: { ...prev.button_labels, [moduleId]: label }
    }));
  };

  const handleButtonUrlChange = (moduleId, url) => {
    if (disabled) return;
    setSettings(prev => ({
      ...prev,
      button_urls: { ...prev.button_urls, [moduleId]: url }
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-gray-600">Loading Popup Offers settings...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Message */}
      {message && (
        <div className={`flex items-center gap-2 p-4 rounded-lg ${
          message.type === "success" 
            ? "bg-green-50 border border-green-200 text-green-800" 
            : "bg-red-50 border border-red-200 text-red-800"
        }`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Disabled Warning */}
      {disabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Dealer is disabled</span>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            Popup Offers settings cannot be modified while the dealer is disabled.
          </p>
        </div>
      )}

      {/* iter121 — Enable / Mode card. Renamed from "Enable Smart Return Offers"
          to "Enable Popup Offers"; below the master toggle the dealer picks
          which audience sees the popup. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Enable Popup Offers
          </CardTitle>
          <CardDescription>
            Show a conversion popup to visitors browsing inventory. Choose below whether to target every visitor or only returning shoppers.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{settings.enabled ? "Enabled" : "Disabled"}</p>
              <p className="text-sm text-gray-500">
                {settings.enabled
                  ? (settings.trigger_mode === "every_visitor"
                      ? "Every visitor on a targeted page will see the popup"
                      : "Only returning visitors past the inactivity gap will see the popup")
                  : "No popup offers will be shown"}
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => handleChange("enabled", checked)}
              disabled={disabled}
              data-testid="popup-offers-master-toggle"
            />
          </div>

          {/* Trigger mode — only meaningful when the master toggle is on. */}
          {settings.enabled && (
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
              <p className="text-sm font-medium text-gray-900">Who should see the popup?</p>

              <label
                htmlFor="popup-mode-every"
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  settings.trigger_mode === "every_visitor"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <input
                  id="popup-mode-every"
                  type="radio"
                  name="popup-mode"
                  value="every_visitor"
                  checked={settings.trigger_mode === "every_visitor"}
                  onChange={() => handleChange("trigger_mode", "every_visitor")}
                  disabled={disabled}
                  className="mt-1 h-4 w-4 text-blue-600"
                  data-testid="popup-mode-every-visitor"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Show to every visitor</p>
                  <p className="text-sm text-gray-500">
                    The popup fires for every shopper on a targeted page (subject to the cooldown below). Best for top-of-funnel offers and incentive campaigns.
                  </p>
                </div>
              </label>

              <label
                htmlFor="popup-mode-returning"
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  settings.trigger_mode === "returning_only"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <input
                  id="popup-mode-returning"
                  type="radio"
                  name="popup-mode"
                  value="returning_only"
                  checked={settings.trigger_mode === "returning_only"}
                  onChange={() => handleChange("trigger_mode", "returning_only")}
                  disabled={disabled}
                  className="mt-1 h-4 w-4 text-blue-600"
                  data-testid="popup-mode-returning-only"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Smart Return Offers (returning visitors only)</p>
                  <p className="text-sm text-gray-500">
                    The popup only fires for shoppers who come back after the inactivity gap below and have meaningful engagement history. Best for win-back / reactivation.
                  </p>
                </div>
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trigger Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Trigger Settings
          </CardTitle>
          <CardDescription>Configure when offers should appear</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Inactivity Gap (hours)</Label>
              <Input
                type="number"
                min="1"
                max="168"
                value={settings.gap_hours}
                onChange={(e) => handleChange("gap_hours", parseInt(e.target.value) || 24)}
                disabled={disabled}
              />
              <p className="text-xs text-gray-500 mt-1">Show offer after visitor has been away</p>
            </div>
            <div>
              <Label>Cooldown Period (hours)</Label>
              <Input
                type="number"
                min="1"
                max="720"
                value={settings.cooldown_hours}
                onChange={(e) => handleChange("cooldown_hours", parseInt(e.target.value) || 72)}
                disabled={disabled}
              />
              <p className="text-xs text-gray-500 mt-1">Don&apos;t show again after display</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-2">
            <div>
              <p className="font-medium">Require prior engagement</p>
              <p className="text-sm text-gray-500">Only show to visitors who previously viewed vehicles</p>
            </div>
            <Switch
              checked={settings.intent_threshold_enabled}
              onCheckedChange={(checked) => handleChange("intent_threshold_enabled", checked)}
              disabled={disabled}
            />
          </div>
        </CardContent>
      </Card>

      {/* Page Targeting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Page Targeting
          </CardTitle>
          <CardDescription>Choose which pages show the return offer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: "target_inventory", label: "Inventory Pages", desc: "SRP / Search results" },
            { key: "target_vdp", label: "Vehicle Detail Pages", desc: "VDP / Single vehicle" },
            { key: "target_homepage", label: "Homepage", desc: "Main landing page" },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <p className="font-medium">{label}</p>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
              <Switch
                checked={settings[key]}
                onCheckedChange={(checked) => handleChange(key, checked)}
                disabled={disabled}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* CTA Modules with URLs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            CTA Modules
          </CardTitle>
          <CardDescription>
            Select up to 4 call-to-action buttons. Add custom labels and destination URLs for each.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {CTA_MODULES.map((module) => {
            const Icon = module.icon;
            const isEnabled = (settings.enabled_modules || []).includes(module.id);
            const currentLabel = settings.button_labels?.[module.id] || module.label;
            const currentUrl = settings.button_urls?.[module.id] || "";
            
            return (
              <div 
                key={module.id} 
                className={`p-4 rounded-lg border-2 transition-colors ${
                  isEnabled 
                    ? "border-blue-200 bg-blue-50" 
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isEnabled ? "bg-blue-100" : "bg-gray-200"}`}>
                      <Icon className={`w-5 h-5 ${isEnabled ? "text-blue-600" : "text-gray-500"}`} />
                    </div>
                    <div>
                      <p className="font-medium">{module.label}</p>
                      <p className="text-sm text-gray-500">{module.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => handleModuleToggle(module.id)}
                    disabled={disabled}
                  />
                </div>
                
                {isEnabled && (
                  <div className="space-y-3 pt-3 border-t border-blue-200">
                    <div>
                      <Label className="text-sm">Button Label</Label>
                      <Input
                        value={currentLabel}
                        onChange={(e) => handleButtonLabelChange(module.id, e.target.value)}
                        placeholder={module.label}
                        disabled={disabled}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" />
                        {module.id === 'sales_contact' ? 'Phone Number or Website URL' : 'Destination URL (optional)'}
                      </Label>
                      {module.id === 'sales_contact' ? (
                        <>
                          <Input
                            type="text"
                            value={currentUrl}
                            onChange={(e) => {
                              let value = e.target.value;
                              // If it looks like a phone number (only digits, dashes, parens, spaces)
                              if (/^[\d\s\-()]+$/.test(value) && !value.includes('http')) {
                                // Strip to just digits
                                value = value.replace(/\D/g, '');
                                // Limit to 10 digits
                                if (value.length > 10) value = value.slice(0, 10);
                                // Format as (XXX) XXX-XXXX
                                if (value.length >= 6) {
                                  value = `(${value.slice(0,3)}) ${value.slice(3,6)}-${value.slice(6)}`;
                                } else if (value.length >= 3) {
                                  value = `(${value.slice(0,3)}) ${value.slice(3)}`;
                                }
                              }
                              handleButtonUrlChange(module.id, value);
                            }}
                            placeholder="(555) 123-4567 or https://..."
                            disabled={disabled}
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter a 10-digit phone number OR a website URL. Phone numbers will be formatted automatically.
                          </p>
                        </>
                      ) : (
                        <>
                          <Input
                            type="url"
                            value={currentUrl}
                            onChange={(e) => handleButtonUrlChange(module.id, e.target.value)}
                            placeholder="https://example.com/calendar"
                            disabled={disabled}
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Leave empty for default behavior, or add a custom URL (e.g., calendar booking, trade tool)
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Copy / Messaging */}
      <Card>
        <CardHeader>
          <CardTitle>Copy & Messaging</CardTitle>
          <CardDescription>Customize the text shown to returning visitors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Headline</Label>
            <Input
              value={settings.headline}
              onChange={(e) => handleChange("headline", e.target.value)}
              placeholder="Welcome Back!"
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Subtext</Label>
            <Input
              value={settings.subtext}
              onChange={(e) => handleChange("subtext", e.target.value)}
              placeholder="How can we help today?"
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Disclaimer Text (optional)</Label>
            <Input
              value={settings.disclaimer_text || ""}
              onChange={(e) => handleChange("disclaimer_text", e.target.value)}
              placeholder="Terms and conditions apply"
              disabled={disabled}
            />
          </div>
        </CardContent>
      </Card>

      {/* Design Settings (Master Admin Only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Design Settings
          </CardTitle>
          <CardDescription>Configure colors and sizes for the offer display</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={settings.primary_color || "#2563eb"}
                  onChange={(e) => handleChange("primary_color", e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border"
                  disabled={disabled}
                />
                <Input
                  value={settings.primary_color || "#2563eb"}
                  onChange={(e) => handleChange("primary_color", e.target.value)}
                  disabled={disabled}
                  className="font-mono"
                />
              </div>
            </div>
            <div>
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={settings.secondary_color || "#10b981"}
                  onChange={(e) => handleChange("secondary_color", e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border"
                  disabled={disabled}
                />
                <Input
                  value={settings.secondary_color || "#10b981"}
                  onChange={(e) => handleChange("secondary_color", e.target.value)}
                  disabled={disabled}
                  className="font-mono"
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Button Size</Label>
              <select
                value={settings.button_size}
                onChange={(e) => handleChange("button_size", e.target.value)}
                disabled={disabled}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                {SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size.charAt(0).toUpperCase() + size.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Headline Size</Label>
              <select
                value={settings.headline_size}
                onChange={(e) => handleChange("headline_size", e.target.value)}
                disabled={disabled}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                {SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size.charAt(0).toUpperCase() + size.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>See how the offer will appear to returning visitors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-6 bg-gray-50">
            <div 
              className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-6"
              style={{ borderTop: `4px solid ${settings.secondary_color || '#10b981'}` }}
            >
              <h2 
                className={`font-bold text-center mb-2 ${
                  settings.headline_size === 'small' ? 'text-lg' :
                  settings.headline_size === 'large' ? 'text-2xl' : 'text-xl'
                }`}
                style={{ color: settings.primary_color }}
              >
                {settings.headline || "Welcome Back!"}
              </h2>
              <p className="text-gray-600 text-center text-sm mb-4">
                {settings.subtext || "How can we help today?"}
              </p>
              <div className="space-y-2">
                {(settings.enabled_modules || []).map((moduleId, index) => {
                  const module = CTA_MODULES.find(m => m.id === moduleId);
                  const Icon = module?.icon || MessageSquare;
                  const label = settings.button_labels?.[moduleId] || module?.label || moduleId;
                  const buttonColor = index % 2 === 0 ? settings.primary_color : settings.secondary_color;
                  return (
                    <button
                      key={moduleId}
                      className={`w-full flex items-center justify-center gap-2 rounded-lg text-white ${
                        settings.button_size === 'small' ? 'py-2 px-3 text-sm' :
                        settings.button_size === 'large' ? 'py-4 px-6 text-lg' : 'py-3 px-4'
                      }`}
                      style={{ backgroundColor: buttonColor }}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
              {settings.disclaimer_text && (
                <p className="text-xs text-gray-400 text-center mt-4">
                  {settings.disclaimer_text}
                </p>
              )}
            </div>
          </div>
          
          {/* Color legend */}
          <div className="flex items-center justify-center gap-6 mt-3 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: settings.primary_color }}></span>
              <span>Primary</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: settings.secondary_color }}></span>
              <span>Secondary</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveSettings} 
          disabled={disabled || saving}
          className="min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
};

export default DealerSmartOffersTab;
